import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { projectCapabilitiesKey } from "@/app/capabilities/api/query-keys";
import { onboardingKeys } from "@/app/onboarding/api/query-keys";
import { useResourceScope } from "@/lib/resource-context";
import { deleteProjectMember } from "./delete-project-member";
import { projectMembersQueryOptions } from "./get-project-members";
import { projectMembersKeys } from "./query-keys";
import { updateProjectMemberRole } from "./update-project-member-role";

export const useGetProjectMembers = () => {
  const scope = useResourceScope();

  return useSuspenseQuery({
    ...projectMembersQueryOptions(scope.organizationId, scope.projectId),
  });
};

export const useDeleteProjectMember = () => {
  const scope = useResourceScope();
  return useMutation({
    mutationFn: ({ memberId }: { memberId: string }) =>
      deleteProjectMember({ memberId, scope }),
    meta: {
      invalidates: [
        projectMembersKeys.list({ scope }),
        onboardingKeys.eligibility({ scope }),
      ],
      errorMessage: "Failed to remove member",
    },
    onSuccess: () => {
      toast.success("Member removed successfully");
    },
  });
};

export const useUpdateProjectMemberRole = () => {
  const scope = useResourceScope();
  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) =>
      updateProjectMemberRole({ memberId, role, scope }),
    meta: {
      // Refetch project capabilities too: a self-demotion drops the caller's
      // authz.permissions, so CanProject/canProject must re-evaluate rather than
      // keep showing admin-only UI from the cached permission set.
      invalidates: [
        projectMembersKeys.list({ scope }),
        projectCapabilitiesKey(scope),
      ],
      errorMessage: "Failed to change role",
    },
    onSuccess: () => {
      toast.success("Role updated successfully");
    },
  });
};
